import React, {
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useMemo,
  useEffect,
} from "react";
import { Tabs } from "antd";
import styles from "./index.module.scss";
import { AvailableGiftCardItem } from "../../constants";
import { availableGiftCards, availableDiscountCards } from "../../constants";
import classNames from "classnames";
import { sendMessageToParent } from "../../utils";
import { IDiscountItem, IPrizeCoupon } from "../../types";
import DiscountCardItem from "./components/DiscountCardItem";
import CouponsItem from "./components/CouponsItem";
import GiftCardItem, { IGiftCardItem } from "./components/GiftCardItem";
import AllPointsActivities from "../PointsActivities/AllPointsActivities";
import { useBottomShadow } from "./useBottomShadow";
import MyCoupons from "./components/MyCoupons";

export interface PointItem {
  available_points: number;
  customer_id: number;
  frozen_points: number;
  id: number;
  total_points: number;
  total_save_amount: number;
}

export interface DiscountItem {
  available_points: number;
  customer_id: number;
  frozen_points: number;
  id: number;
  total_points: number;
  total_save_amount: number;
}

interface PointsAndCouponsProps {
  points: PointItem[];
  coupons: IPrizeCoupon[];
  couponsLoading?: boolean;
  discountCards: IDiscountItem[];
  giftCards: IGiftCardItem[];
  customerId: number;
  onRedeem?: (data: AvailableGiftCardItem) => void;
}

// 定义暴露给父组件的方法接口
export interface PointsAndCouponsRef {
  scrollIntoView: () => void;
  switchTab: (tab: string) => void;
}

const PointsAndCoupons = forwardRef<PointsAndCouponsRef, PointsAndCouponsProps>(
  (
    {
      points,
      coupons,
      couponsLoading = false,
      discountCards,
      giftCards,
      customerId,
    },
    ref,
  ) => {
    const [activeTab, setActiveTab] = useState<string>("points");
    const [showGiftCardNotification, setShowGiftCardNotification] =
      useState<boolean>(false);
    const notificationKey = useMemo(() => `showGiftCardNotification`, []);
    // 创建内部 ref 来引用 DOM 元素
    const innerRef = useRef<HTMLDivElement>(null);
    const pointsActivitiesRef = useRef<HTMLDivElement>(null);
    const GiftCardEmpty = useCallback(() => {
      return (
        <div className={styles.empty}>
          <p>No points redemption history yet ~</p>
        </div>
      );
    }, []);
    const scrollBoxRef = useRef(null);
    const { handleScrollBottom, initReachBottom, reachBottom } =
      useBottomShadow(scrollBoxRef);
    const myCouponsScrollBoxRef = useRef(null);
    const {
      handleScrollBottom: handleMyCouponsScrollBottom,
      initReachBottom: initMyCouponsReachBottom,
      reachBottom: myCouponsReachBottom,
    } = useBottomShadow(myCouponsScrollBoxRef);

    const items = useMemo(
      () => [
        {
          key: "points",
          label: <div data-element-id="tabs-redeem-points">Points Details</div>,
          children: (
            <>
              <div>
                <AllPointsActivities
                  customerId={customerId}
                ></AllPointsActivities>
              </div>
            </>
          ),
        },
        {
          key: "rewards",
          label: (
            <div
              className={styles.discountCardLabel}
              data-element-id="tabs-discount-card"
            >
              Member Rewards
              {showGiftCardNotification && (
                <span className={styles.notificationPoint}></span>
              )}
            </div>
          ),
          children: (
            <div
              onScroll={handleScrollBottom}
              className={`${styles.couponsListContent}  ${
                reachBottom ? styles.hiddenBottomShadow : ""
              }`}
              ref={scrollBoxRef}
            >
              <div
                className={classNames(
                  styles.tabContent,
                  discountCards.length + giftCards.length === 0
                    ? styles.emptyLayout
                    : "",
                )}
              >
                <>
                  {/* 折扣券、免邮券 */}
                  {discountCards.length > 0
                    ? discountCards.map((card) => {
                        if (card.discount_code_type === 2) {
                          return <CouponsItem key={card.id} data={card} />;
                        }

                        return (
                          <DiscountCardItem
                            key={card.id}
                            data={card}
                            availableGiftCards={availableDiscountCards}
                          />
                        );
                      })
                    : null}

                  {/* gift card */}
                  {giftCards.length > 0
                    ? giftCards.map((card) => (
                        <GiftCardItem
                          key={card.id}
                          data={card}
                          availableGiftCards={availableGiftCards}
                        />
                      ))
                    : null}

                  {/* 没有数据时展示 */}
                  {giftCards.length === 0 && discountCards.length === 0 && (
                    <GiftCardEmpty />
                  )}
                </>
              </div>
            </div>
          ),
        },
        {
          key: "coupons",
          label: <div data-element-id="tabs-my-coupons">My Coupons</div>,
          children: (
            <div
              onScroll={handleMyCouponsScrollBottom}
              className={`${styles.couponsListContent} ${
                myCouponsReachBottom ? styles.hiddenBottomShadow : ""
              }`}
              ref={myCouponsScrollBoxRef}
            >
              <MyCoupons coupons={coupons} loading={couponsLoading} />
            </div>
          ),
        },
      ],
      [
        handleScrollBottom,
        reachBottom,
        discountCards,
        giftCards,
        showGiftCardNotification,
        customerId,
        GiftCardEmpty,
        coupons,
        couponsLoading,
        handleMyCouponsScrollBottom,
        myCouponsReachBottom,
      ],
    );

    const handleChangeTab = useCallback(
      (key: string) => {
        console.log(">>>> key:", key);

        sendMessageToParent({
          type: "send-datac",
          data: {
            elementId: `tabs-${key}`,
          },
        });

        setActiveTab(key);

        requestAnimationFrame(initReachBottom);
        if (key === "coupons") {
          requestAnimationFrame(initMyCouponsReachBottom);
        }
        // initReachBottom();

        // 切换到 giftCard 的时候，移除 notificationKey
        if (key === "rewards") {
          localStorage.removeItem(notificationKey);
          setShowGiftCardNotification(false);
        }

        // 每次切换的时候，都把主页上的 toast 隐藏
        sendMessageToParent({
          type: "member-toast-hide",
        });
      },
      [notificationKey, initReachBottom, initMyCouponsReachBottom],
    );

    useImperativeHandle(ref, () => ({
      scrollIntoView: () => {
        setTimeout(() => {
          innerRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 200);
      },
      getElementPosition: () => {
        const rect = innerRef.current?.getBoundingClientRect();
        return {
          top: (rect?.top || 0) + window.pageYOffset + 90 - 45, // 减去 iframe 前面内容的高度 90px
          left: (rect?.left || 0) + window.pageXOffset, // 相对于文档左侧的 X 坐标
          width: rect?.width || 0,
          height: rect?.height || 0,
        };
      },
      getPointsActivitiesPosition: () => {
        const rect = pointsActivitiesRef.current?.getBoundingClientRect();
        return {
          top: (rect?.top || 0) + window.pageYOffset + 90, // 减去 iframe 前面内容的高度 90px
          left: (rect?.left || 0) + window.pageXOffset,
          width: rect?.width || 0,
          height: rect?.height || 0,
        };
      },
      switchTab: (tab: string) => {
        console.log(">>>> tab:", tab);
        setActiveTab(tab);
      },
    }));

    // 初始化 giftCard 的 notificationPoint 状态
    useEffect(() => {
      const isShowNotification = localStorage.getItem(notificationKey);
      setShowGiftCardNotification(!!isShowNotification);
    }, [notificationKey]);

    return (
      <div ref={innerRef} className={styles.pointsAndCoupons}>
        <Tabs
          defaultActiveKey="points"
          activeKey={activeTab}
          className={styles.antTabs}
          items={items}
          onChange={(key) => handleChangeTab(key)}
        />
      </div>
    );
  },
);

export default PointsAndCoupons;
